import React, { useState, useEffect } from "react";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import DynamicFromLookup from "../../../../dynamicFormBuilder/DynamicFromLookup";
import { WMLotByItemLookupLayout, WMSublotByLotLookupLayout } from "../../../../common/lookups/Layouts/WMLayouts";
import { DynamicLookupOptions } from "../../../../enum/Enum";

const FILTER_TABLE = "IMLOT";
const SUBLOT_TABLE = "IMSUBLOT";

// Migrated from Delphi WMD0098 ("Filter Item"). Optional lot filter for the Find-Lots grid:
// pick a single lot of the original item, or leave blank to retrieve all lots. WMD0128 only
// consumes the selected Lot (the sublot filter in the legacy form is not used here).
const TransferLotsFilterPopup = ({ visible, itemRowId, itemCode, itemDesc1, itemDesc2, onOk, onCancel }) => {
    const [lotFields, setLotFields] = useState({ R_Lot: "" });
    const [selectedLot, setSelectedLot] = useState({ desc1: "", desc2: "" });
    const [sublotFields, setSublotFields] = useState({ R_Sublot: "" });
    const [selectedSublot, setSelectedSublot] = useState({ desc1: "", desc2: "" });

    // Reset the lot/sublot selection each time the popup is (re)opened.
    useEffect(() => {
        if (visible) {
            setLotFields({ R_Lot: "" });
            setSelectedLot({ desc1: "", desc2: "" });
            setSublotFields({ R_Sublot: "" });
            setSelectedSublot({ desc1: "", desc2: "" });
        }
    }, [visible]);

    const lotLayout = WMLotByItemLookupLayout(itemRowId);
    // Sublots are scoped to the selected lot; the layout recomputes when the lot changes.
    const sublotLayout = WMSublotByLotLookupLayout(lotFields.R_Lot);
    const lotField = {
        name: "R_Lot",
        tableName: FILTER_TABLE,
        label: "Lot",
        type: "lookup",
        layout: lotLayout,
        required: false,
        isLabelChanged: true,
        // Binocular grid mode: the field shows the lot code while the bound value stays the
        // lot RowId (the SelectBox mode has a valueField bug that renders the raw GUID).
        lookupDisplayMode: DynamicLookupOptions.GridDisplayMode,
    };

    const handleLotChange = (value) => {
        setLotFields({ R_Lot: value || "" });
        // Sublots belong to a lot — clear the sublot selection whenever the lot changes.
        setSublotFields({ R_Sublot: "" });
        setSelectedSublot({ desc1: "", desc2: "" });
        if (!value) {
            setSelectedLot({ desc1: "", desc2: "" });
        }
    };

    // SelectBox passes the DevExtreme event (row in e.selectedItem); the grid passes the row directly.
    const handleLotSelection = (name, e) => {
        const row = e?.selectedItem ?? e;
        if (row) {
            setSelectedLot({ desc1: row.description1 || "", desc2: row.description2 || "" });
        }
    };

    const handleSublotChange = (value) => {
        setSublotFields({ R_Sublot: value || "" });
        if (!value) {
            setSelectedSublot({ desc1: "", desc2: "" });
        }
    };

    const handleSublotSelection = (name, e) => {
        const row = e?.selectedItem ?? e;
        if (row) {
            setSelectedSublot({ desc1: row.description1 || "", desc2: row.description2 || "" });
        }
    };

    const sublotField = {
        name: "R_Sublot",
        tableName: SUBLOT_TABLE,
        label: "SubLot",
        type: "lookup",
        layout: sublotLayout,
        required: false,
        isLabelChanged: true,
        lookupDisplayMode: DynamicLookupOptions.GridDisplayMode,
    };

    const staticValue = { color: "#555", padding: "4px 0", minHeight: 20 };
    const labelCol = { width: 90, fontWeight: "bold", color: "#3a76b8" };

    return (
        <Popup
            visible={visible}
            onHiding={onCancel}
            title="Filter Item"
            width={520}
            height={560}
            showCloseButton={true}
            dragEnabled={true}
        >
            <ToolbarItem toolbar="bottom" location="center">
                <Button className="accordianButton" type="default" text="OK" onClick={() => onOk(lotFields.R_Lot || "", sublotFields.R_Sublot || "")} />
            </ToolbarItem>
            <ToolbarItem toolbar="bottom" location="center">
                <Button className="accordianButton" text="Cancel" onClick={onCancel} />
            </ToolbarItem>

            <p style={{ fontSize: 12, color: "#666", margin: "0 0 12px" }}>
                You may select a specific Lot. By leaving it blank and clicking OK, all Lots for the
                selected Item will be retrieved.
            </p>

            {/* Item (read-only) */}
            <fieldset style={{ border: "1px solid #ddd", borderRadius: 4, padding: "8px 12px", marginBottom: 12 }}>
                <legend style={{ fontSize: 13, fontWeight: "bold", width: "auto", padding: "0 6px" }}>Item</legend>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={labelCol}>Item</span>
                    <span style={staticValue}>{itemCode || ""}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={labelCol}>Description 1</span>
                    <span style={staticValue}>{itemDesc1 || ""}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={labelCol}>Description 2</span>
                    <span style={staticValue}>{itemDesc2 || ""}</span>
                </div>
            </fieldset>

            {/* Lot filter (dynamic lookup — SelectBox + binocular grid) */}
            <fieldset style={{ border: "1px solid #ddd", borderRadius: 4, padding: "8px 12px" }}>
                <legend style={{ fontSize: 13, fontWeight: "bold", width: "auto", padding: "0 6px" }}>Lot</legend>
                <div style={{ marginBottom: 6 }}>
                    <DynamicFromLookup
                        fieldRef={() => { }}
                        name={lotField.name}
                        required={false}
                        field={lotField}
                        formFields={lotFields}
                        layout={lotLayout}
                        onValueChanged={(e) => handleLotChange(e.value)}
                        editMode={false}
                        handleSelectionChange={handleLotSelection}
                    />
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={labelCol}>Description 1</span>
                    <span style={staticValue}>{selectedLot.desc1}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={labelCol}>Description 2</span>
                    <span style={staticValue}>{selectedLot.desc2}</span>
                </div>
            </fieldset>

            {/* SubLot filter (scoped to the selected Lot) */}
            <fieldset style={{ border: "1px solid #ddd", borderRadius: 4, padding: "8px 12px", marginTop: 12 }}>
                <legend style={{ fontSize: 13, fontWeight: "bold", width: "auto", padding: "0 6px" }}>SubLot</legend>
                <div style={{ marginBottom: 6 }}>
                    <DynamicFromLookup
                        fieldRef={() => { }}
                        name={sublotField.name}
                        required={false}
                        field={sublotField}
                        formFields={sublotFields}
                        layout={sublotLayout}
                        onValueChanged={(e) => handleSublotChange(e.value)}
                        editMode={false}
                        handleSelectionChange={handleSublotSelection}
                    />
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={labelCol}>Description 1</span>
                    <span style={staticValue}>{selectedSublot.desc1}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={labelCol}>Description 2</span>
                    <span style={staticValue}>{selectedSublot.desc2}</span>
                </div>
            </fieldset>
        </Popup>
    );
};

export default TransferLotsFilterPopup;
